import React from "react";

import { useExperience } from "@/context/ExperienceContext";

export const useParseLocalizedPathname = () => {
  const { alternates, country: currentCountry, language: currentLanguage } = useExperience();

  return React.useMemo(() => {
    const parseLocalizedPathname = (pathname?: string) => {
      if (!pathname || !pathname.startsWith("/")) {
        return { pathname };
      }

      // Strip query/hash before splitting into segments - otherwise a path like
      // "/us#ExploreByAge" (no slash separating the country segment from the hash, as
      // Next.js router produces when pushing a hash-only href) would parse the country
      // segment as "us#exploreByage" instead of "us", breaking country/language matching.
      let pathnameOnly = pathname;
      let pathnameSuffix = "";
      try {
        const parsedInput = new URL(pathname, "https://stub.domain");
        pathnameOnly = parsedInput.pathname;
        pathnameSuffix = parsedInput.search + parsedInput.hash;
      } catch {
        // Ignore that the path couldn't be parsed
      }

      const parts = pathnameOnly.split("/");
      const countryPart = parts[1]?.toLowerCase();
      const languagePart = parts[2]?.toLowerCase();
      const countryAlternates = alternates?.filter(
        (alternate) => alternate.country === countryPart
      );
      // Single-locale profiles (e.g. the US-only portfolio pages) have no sibling
      // country/language route variants, so `alternates` is empty and the lookup above
      // never matches - fall back to the current experience's own resolved country/language
      // so hrefs still get the right locale segment recognized/propagated.
      const countryBasename =
        countryAlternates?.[0]?.country ??
        (!alternates?.length && countryPart === currentCountry ? currentCountry : undefined);
      const languageAlternate = countryAlternates?.find(
        (alternate) => alternate.language === languagePart
      );
      const languageBasename =
        languageAlternate?.language ??
        (!alternates?.length && countryBasename && languagePart === currentLanguage
          ? currentLanguage
          : undefined);
      const pathnameWithoutLocale = parts
        .slice(countryBasename && languageBasename ? 3 : countryBasename ? 2 : 1)
        .join("/");
      let pathnameResolved = pathnameWithoutLocale;

      try {
        const url = new URL(pathnameWithoutLocale, "https://stub.domain");
        pathnameResolved = url.pathname.toLowerCase() + url.search + url.hash + pathnameSuffix;
      } catch {
        // Ignore that the path couldn't be parsed
      }

      return {
        country: countryBasename,
        language: languageBasename,
        pathname: pathnameResolved
      };
    };

    return {
      getLocalizedPathnamesMatch: (pathname1?: string, pathname2?: string) => {
        if (!pathname1 || !pathname2) {
          return false;
        }

        const parsed1 = parseLocalizedPathname(pathname1);
        const parsed2 = parseLocalizedPathname(pathname2);

        if (parsed1.pathname === undefined || parsed2.pathname === undefined) {
          return false;
        }

        try {
          const MOCK_DOMAIN = "https://m.co";
          const url1 = new URL(parsed1.pathname, MOCK_DOMAIN);
          const url2 = new URL(parsed2.pathname, MOCK_DOMAIN);
          return url1.pathname.replace(/\/$/, "") === url2.pathname.replace(/\/$/, "");
        } catch {
          return false;
        }
      },
      parseLocalizedPathname
    };
  }, [alternates, currentCountry, currentLanguage]);
};
